import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { TimeChanged, TimeSheet } from '../../../../types/Reports/ShiftEnd/TimeSheet'
import { Activity } from '../../../../types/Reports/ShiftEnd/Activity'
import { ForemanDB } from '../../../../types/IndexedDB/ForemanDB'
import {
	updateTimeSheetActivities,
	updateTimeRecordComment,
} from '../../../../services/shiftEndReportService'
import {
	activityMatchesSearch,
	activitySpansMidnight,
	calculateHours,
	findStartEndTime,
	matchesSearch,
	normalizeActivityDates,
	normalizeTimeDiffForOvernight,
	timeCascadeActivities,
	timeOfDaySeconds,
	workSheetTotalHoursCalc,
} from '../../../../utils/TimesheetUtils'

type UseShiftEndTimeSheetsArgs = {
	workerSummary: TimeSheet[]
	setWorkersTimeRecords: React.Dispatch<React.SetStateAction<TimeSheet[]>>
	shiftDate: Date
	db: ForemanDB | null
	searchText: string
	includeAllTasks: boolean
}

export const useShiftEndTimeSheets = ({
	workerSummary,
	setWorkersTimeRecords,
	shiftDate,
	db,
	searchText,
	includeAllTasks,
}: UseShiftEndTimeSheetsArgs) => {
	const filteredData = useMemo(() => {
		if (searchText === '') return [...workerSummary]
		const searchLower = searchText.toLowerCase()
		const filtered = (workerSummary || []).filter((x) => matchesSearch(searchLower, x))
		if (!includeAllTasks) {
			return filtered.map((timeSheet) => ({
				...timeSheet,
				activities: timeSheet.activities.filter((t) => activityMatchesSearch(searchLower, t)),
			}))
		}
		return filtered
	}, [workerSummary, searchText, includeAllTasks])

	const updateTimeSheet = useCallback(
		async (timesheet: TimeSheet, activity: Activity, startTime: Date, endTime: Date | null) => {
			const workerSummaryCopy = workerSummary.map((ts) => ({
				...ts,
				activities: ts.activities.map((a) => ({ ...a })),
			}))

			const indexOfNewTimeSheet = workerSummaryCopy.findIndex((i) => i.id === timesheet.id)
			if (indexOfNewTimeSheet === -1) return

			const sheet = workerSummaryCopy[indexOfNewTimeSheet]
			const foundActivity = sheet.activities.find((el) => el.id === activity.id)
			if (!foundActivity) return

			const isLastActivity = sheet.activities.indexOf(foundActivity) === sheet.activities.length - 1
			const startUnchanged = dayjs(startTime).isSame(dayjs(foundActivity.startTime))
			const endInvalid = !dayjs(foundActivity.endTime).isValid()

			if (isLastActivity && startUnchanged && endInvalid) {
				foundActivity.endTime = endTime
			} else {
				let startTimeDiff = dayjs(startTime).diff(dayjs(foundActivity.startTime), 'second')
				let endTimeDiff = endTime ? dayjs(endTime).diff(dayjs(foundActivity.endTime), 'second') : 0
				const overnight =
					activitySpansMidnight(foundActivity.startTime, foundActivity.endTime) ||
					(endTime != null && timeOfDaySeconds(endTime) < timeOfDaySeconds(foundActivity.startTime))
				if (overnight) {
					startTimeDiff = normalizeTimeDiffForOvernight(startTimeDiff)
					endTimeDiff = normalizeTimeDiffForOvernight(endTimeDiff)
				}
				const updatedActivityIndex = sheet.activities.indexOf(foundActivity)
				const timeChanged =
					startTimeDiff === 0 ? TimeChanged.EndTimeChanged : TimeChanged.StartTimeChanged
				sheet.activities = timeCascadeActivities(
					sheet.activities,
					timeChanged === TimeChanged.StartTimeChanged ? startTimeDiff : endTimeDiff,
					timeChanged,
					updatedActivityIndex,
				)
			}

			normalizeActivityDates(sheet.activities, shiftDate)

			sheet.activities.forEach((act) => {
				act.hours = act.endTime
					? Number(calculateHours(act.startTime, act.endTime).toFixed(2))
					: null
			})

			sheet.hours = Number(workSheetTotalHoursCalc(sheet.activities).toFixed(2))
			const { minStartTime, maxEndTime } = findStartEndTime(sheet)
			sheet.startTime = minStartTime
			sheet.endTime = maxEndTime

			await updateTimeSheetActivities(sheet.id, sheet.activities, db)
			setWorkersTimeRecords(workerSummaryCopy)
		},
		[workerSummary, setWorkersTimeRecords, shiftDate, db],
	)

	const updateActivityComment = useCallback(
		async (timesheetId: number, timeRecordId: number, comment: string) => {
			await updateTimeRecordComment(timesheetId, timeRecordId, comment, db)
			setWorkersTimeRecords((prev) =>
				prev.map((ts) =>
					ts.id !== timesheetId
						? ts
						: {
								...ts,
								activities: ts.activities.map((a) =>
									a.id === timeRecordId ? { ...a, comment } : a,
								),
						  },
				),
			)
		},
		[db, setWorkersTimeRecords],
	)

	return { updateTimeSheet, updateActivityComment, filteredData }
}
